/**
 * Reviews Section.
 *
 * Builds the REVIEWS.io Card Carousel inside the Reviews Section on the home page.
 * The vendor script defines the global `carouselInlineWidget`, and the widget draws
 * its own cards inside the container this file finds.
 *
 * The section markup carries the merchant settings as data attributes, because
 * Liquid tags cannot run inside an asset file.
 *
 * This file fetches the vendor stylesheets and script itself, and only once the
 * section nears the viewport. The section sits far down the home page, so loading
 * them in the markup would put three third-party requests on the critical path of
 * every home page view for something most visitors never scroll to.
 *
 * A MutationObserver reports when the cards exist, because the widget offers no callback.
 * https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
 *
 * The `styles` object below is not a stylesheet. The widget copies each entry onto
 * its own root element as a CSS custom property, so the values reuse the theme
 * tokens from `assets/tokens.css`. The widget renders in the page, not in an
 * iframe, so `var()` resolves against the theme.
 *
 * Widget docs: https://support.reviews.io/en/articles/9184959-card-carousel-widget
 */
(function () {
  const CONTAINER_SELECTOR = '[aura-home-reviews]';
  const CARD_BUTTON_SELECTOR = '.R-ReviewsList__item[role="button"]';

  const VENDOR_STYLESHEETS = [
    'https://assets.reviews.io/css/widgets/carousel-widget.css',
    'https://assets.reviews.io/iconfont/reviewsio-icons/style.css',
  ];
  const VENDOR_SCRIPT = 'https://widget.reviews.io/carousel-inline-iframeless/dist.js';

  const PRELOAD_MARGIN = '1000px';
  const RENDER_TIMEOUT = 10000;

  const DEFAULT_MAX_REVIEWS = 20;
  const DEFAULT_AUTO_SCROLL_DELAY = 3000;
  const DEFAULT_LANG = 'en';

  let vendorRequest = null;

  const WIDGET_STYLES = {
    '--common-star-color': 'var(--aura-color-amber-400)',
    '--common-star-disabled-color': 'var(--aura-color-amber-400)',

    '--header-width': '0px',

    '--item-padding': '40px 28px',

    '--body-text-color': 'var(--aura-color-stone-600)',
    '--body-max-lines': '10',
  };

  /**
   * Fetch the REVIEWS.io stylesheets and script. Runs its work once per page.
   *
   * @returns {Promise<void>} Settles once the script has loaded or failed.
   */
  const loadVendorAssets = () => {
    // Do not proceed if the request already started.
    if (vendorRequest) {
      return vendorRequest;
    }

    VENDOR_STYLESHEETS.forEach((href) => {
      const linkEl = document.createElement('link');
      linkEl.rel = 'stylesheet';
      linkEl.href = href;
      document.head.appendChild(linkEl);
    });

    vendorRequest = new Promise((resolve) => {
      const scriptEl = document.createElement('script');
      scriptEl.src = VENDOR_SCRIPT;
      scriptEl.addEventListener('load', resolve);
      scriptEl.addEventListener('error', resolve);
      document.head.appendChild(scriptEl);
    });

    return vendorRequest;
  };

  /**
   * Read the merchant settings the section wrote onto the container.
   *
   * @param {HTMLElement} containerEl - The element the widget builds into.
   * @returns {{store: string, lang: string, reviewType: string, maxReviews: number, autoScroll: (number|boolean)}} The settings.
   */
  const readSettings = (containerEl) => {
    const autoScroll = containerEl.dataset.autoScroll;

    // Without a fallback a missing attribute parses to NaN, and the widget forwards it.
    return {
      store: containerEl.dataset.store,
      lang: containerEl.dataset.lang || DEFAULT_LANG,
      reviewType: containerEl.dataset.reviewType,
      maxReviews: Number.parseInt(containerEl.dataset.maxReviews, 10) || DEFAULT_MAX_REVIEWS,
      autoScroll:
        autoScroll === 'false'
          ? false
          : Number.parseInt(autoScroll, 10) || DEFAULT_AUTO_SCROLL_DELAY,
    };
  };

  /**
   * Take the button role off the cards the widget drew.
   *
   * @param {HTMLElement} containerEl - The element the widget builds into.
   * @returns {void}
   */
  const refineCards = (containerEl) => {
    // The widget stamps role="button" on every card, and popups are off, so it does nothing.
    const cardEls = Array.from(containerEl.querySelectorAll(CARD_BUTTON_SELECTOR));

    cardEls.forEach((cardEl) => {
      cardEl.removeAttribute('role');
      cardEl.removeAttribute('tabindex');
    });
  };

  /**
   * Build the carousel inside one container.
   *
   * @param {HTMLElement} containerEl - The element the widget builds into.
   * @returns {void}
   */
  const buildCarousel = (containerEl) => {
    // Do not proceed if this container already holds a carousel.
    if (containerEl.dataset.carouselBuilt === 'true') {
      return;
    }

    // Do not proceed if the vendor constructor is missing. The flag makes the stylesheet hide it.
    if (typeof carouselInlineWidget === 'undefined') {
      containerEl.dataset.vendorFailed = 'true';
      return;
    }

    const settings = readSettings(containerEl);

    containerEl.dataset.carouselBuilt = 'true';

    // The widget listens for load only, so a failed request draws nothing and reports nothing.
    const renderTimer = setTimeout(() => {
      containerEl.dataset.vendorFailed = 'true';
    }, RENDER_TIMEOUT);

    // Start before the widget, which replaces every direct child on each draw.
    const renderObserver = new MutationObserver(() => {
      clearTimeout(renderTimer);

      // A draw slower than the deadline sets the flag, so the section must come back.
      delete containerEl.dataset.vendorFailed;

      containerEl.dataset.vendorRendered = 'true';
      refineCards(containerEl);
    });
    renderObserver.observe(containerEl, { childList: true });

    new carouselInlineWidget(containerEl.id, {
      store: settings.store,
      lang: settings.lang,

      options: {
        general: {
          review_type: settings.reviewType,
          min_reviews: 1,
          max_reviews: settings.maxReviews,
          enable_auto_scroll: settings.autoScroll,
          enable_pause_button: false,
          enable_richsnippets_company: false,
        },
        reviews: {
          enable_customer_location: false,
          enable_verified_badge: false,
          enable_recommends_badge: false,
          enable_photos: false,
          enable_videos: false,
          enable_review_date: false,
          disable_same_customer: true,
          third_party_source: true,
        },
        popups: {
          enable_review_popups: false,
        },
      },

      styles: WIDGET_STYLES,
    });
  };

  const preloadObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        // Skip the sections still far below the fold.
        if (!entry.isIntersecting) {
          return;
        }

        preloadObserver.unobserve(entry.target);
        loadVendorAssets().then(() => buildCarousel(entry.target));
      });
    },
    { rootMargin: PRELOAD_MARGIN },
  );

  /**
   * Watch every Reviews Section on the page and build each one as it approaches.
   *
   * @returns {void}
   */
  const observeAllCarousels = () => {
    const containers = Array.from(document.querySelectorAll(CONTAINER_SELECTOR));
    containers.forEach((containerEl) => preloadObserver.observe(containerEl));
  };

  if (document.readyState === 'complete') {
    observeAllCarousels();
  } else {
    window.addEventListener('load', observeAllCarousels);
  }

  document.addEventListener('shopify:section:load', (event) => {
    const containerEl = event.target.querySelector(CONTAINER_SELECTOR);

    // Do not proceed if another section reloaded.
    if (!containerEl) {
      return;
    }

    loadVendorAssets().then(() => buildCarousel(containerEl));
  });
})();
